import { queryOptions } from "@tanstack/react-query";
import {
  fetchChangelog,
  fetchChangelogCandidates,
  fetchChangelogDigestEmailPreview,
  fetchChangelogDigests,
  fetchChangelogRecipients,
  fetchChangelogSettings,
  type ChangelogDigestPage,
  type ChangelogPage,
} from "@/features/changelog/api/changelog.api";

/**
 * Deploys are infrequent, so the list is allowed to sit for a while; the
 * refetch that matters is the one driven by `pollAfterMs` while notes generate.
 */
const CHANGELOG_STALE_TIME_MS = 60 * 1000;

/** The mailing list only changes when an admin edits it, so it can sit longer. */
const CHANGELOG_RECIPIENTS_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Settings decide how every other changelog surface behaves — the sprint the
 * deploys filter offers, whether the tab syncs on open — so they are read on
 * more screens than they are written on and can sit for a while.
 */
const CHANGELOG_SETTINGS_STALE_TIME_MS = 5 * 60 * 1000;

export const changelogKeys = {
  all: ["changelog"] as const,
  list: ["changelog", "list"] as const,
  recipients: ["changelog", "recipients"] as const,
  settings: ["changelog", "settings"] as const,
  digests: ["changelog", "digests"] as const,
  candidates: ["changelog", "digests", "candidates"] as const,
} as const;

export function changelogQueryOptions() {
  return queryOptions({
    queryKey: changelogKeys.list,
    queryFn: fetchChangelog,
    staleTime: CHANGELOG_STALE_TIME_MS,
    // Generation runs in a worker, so the tab polls itself until every entry
    // has settled, then stops.
    refetchInterval: (query) => {
      const data = query.state.data as ChangelogPage | undefined;
      return data?.pollAfterMs ?? false;
    },
  });
}

export function changelogRecipientsQueryOptions() {
  return queryOptions({
    queryKey: changelogKeys.recipients,
    queryFn: fetchChangelogRecipients,
    staleTime: CHANGELOG_RECIPIENTS_STALE_TIME_MS,
  });
}

export function changelogSettingsQueryOptions() {
  return queryOptions({
    queryKey: changelogKeys.settings,
    queryFn: fetchChangelogSettings,
    staleTime: CHANGELOG_SETTINGS_STALE_TIME_MS,
  });
}

export function changelogDigestEmailPreviewQueryOptions(digestId: string) {
  return queryOptions({
    queryKey: [...changelogKeys.digests, "email-preview", digestId] as const,
    queryFn: () => fetchChangelogDigestEmailPreview(digestId),
    staleTime: 0,
  });
}

export function changelogDigestsQueryOptions() {
  return queryOptions({
    queryKey: changelogKeys.digests,
    queryFn: fetchChangelogDigests,
    staleTime: CHANGELOG_STALE_TIME_MS,
    refetchInterval: (query) => {
      const data = query.state.data as ChangelogDigestPage | undefined;
      return data?.pollAfterMs ?? false;
    },
  });
}

/**
 * Always refetched on open: the candidate list shrinks as deploys are rolled
 * up, and composing from a stale list means ticking items another roundup has
 * already taken.
 */
export function changelogCandidatesQueryOptions() {
  return queryOptions({
    queryKey: changelogKeys.candidates,
    queryFn: fetchChangelogCandidates,
    staleTime: 0,
  });
}
